import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Jumbotron, Modal, Table } from 'react-bootstrap';
import SystemUtils from '../SystemUtils';
import AtlasMapsPopup from './AtlasMapsPopup';
import AtlasProcessPopup from './AtlasProcessPopup';
import { atlasApi } from './AtlasApi';
import './RecordingsList.css';
import '../Controls/MusterTableStyle.css';
import './Atlas.css';

export default function Atlas({ accessLevel, openAtlas }) {
    const editable = Number(accessLevel) === 1;
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('atlasId');
    const [order, setOrder] = useState('asc');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [selected, setSelected] = useState(null);
    const [processing, setProcessing] = useState(null);
    const [create, setCreate] = useState(false);
    const [name, setName] = useState('');
    const [busy, setBusy] = useState(null);
    const [loading, setLoading] = useState(true);
    const generation = useRef(0);
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; ++generation.current; }, []);
    const refresh = useCallback(async () => {
        const current = ++generation.current;
        try {
            const data = await atlasApi('atlas_list?' + new URLSearchParams({ search, sort, order, offset, limit: 20 }));
            if (mounted.current && current === generation.current) { setRows(data.items); setTotal(data.total); setLoading(false); }
        } catch (e) { if (mounted.current && current === generation.current) { setError(e.message); setLoading(false); } }
    }, [search, sort, order, offset]);
    useEffect(() => {
        refresh();
        const timer = setInterval(refresh, 5000);
        return () => { clearInterval(timer); };
    }, [refresh]);
    const changeSort = field => { setSort(field); setOrder(field === sort && order === 'asc' ? 'desc' : 'asc'); setOffset(0); };
    const createAtlas = async event => {
        event.preventDefault(); setBusy('create'); setError('');
        try {
            const result = await atlasApi('atlas_create', { atlasName: name.trim() });
            setCreate(false); setName(''); setSelected(result); refresh();
        } catch (e) { setError(e.message); }
        finally { if (mounted.current) setBusy(null); }
    };
    const merge = async atlas => {
        setBusy(atlas.atlasId); setError(''); setNotice('');
        try {
            await atlasApi('atlas_merge', { atlasId: atlas.atlasId, revision: atlas.revision });
            setNotice('AtlasMerge queued: ' + atlas.atlasName + '. Monitor progress in Pipeline.');
            await wake();
        } catch (e) { setError(e.message); }
        finally { if (mounted.current) { setBusy(null); refresh(); } }
    };
    const wake = async () => {
        const response = await fetch('process_executor', { method: 'POST' });
        if (!response.ok) throw new Error('The atlas is queued. Select Start pipeline to retry starting it.');
    };
    const open = async atlas => {
        setBusy(atlas.atlasId); setError('');
        try { const descriptor = await atlasApi('atlas_viewer?atlasId=' + atlas.atlasId); await openAtlas(descriptor); }
        catch (e) { if (mounted.current) setError(e.message); }
        finally { if (mounted.current) setBusy(null); }
    };
    return <div>
        <Jumbotron className="jumboHeader"><h1 className="pageTitle text-center">Atlas</h1></Jumbotron>
        <div className="atlasToolbar">
            {editable && <Button size="lg" onClick={() => { setCreate(true); setError(''); }}>New Atlas</Button>}
            <Form.Control aria-label="Search atlases" placeholder="Search atlas names" value={search} onChange={e => { setSearch(e.target.value); setOffset(0); }} />
            <Button variant="outline-secondary" disabled={!offset} onClick={() => setOffset(offset - 20)}>Previous</Button>
            <span>{total ? offset + 1 : 0}–{Math.min(offset + 20, total)} of {total}</span>
            <Button variant="outline-secondary" disabled={offset + 20 >= total} onClick={() => setOffset(offset + 20)}>Next</Button>
        </div>
        {error && !create && <Alert variant="danger">{error}</Alert>}
        {notice && <Alert variant="success">{notice}</Alert>}
        <div className="table-responsive"><Table className="cloudListTable musterStyleTable atlasTable">
            <thead><tr>
                <th aria-sort={sort === 'atlasId' ? order === 'asc' ? 'ascending' : 'descending' : 'none'}><Button variant="link" onClick={() => changeSort('atlasId')}>AtlasId {sort === 'atlasId' ? (order === 'asc' ? '↑' : '↓') : ''}</Button></th>
                <th><Button variant="link" onClick={() => changeSort('atlasName')}>Atlas Name</Button></th>
                <th><h3>Atlas Maps</h3></th><th><Button variant="link" onClick={() => changeSort('sizeBytes')}>Size</Button></th>
                <th><h3>Process</h3></th><th><h3>View Atlas</h3></th><th><h3>Merge</h3></th>
            </tr></thead>
            <tbody>{rows.map(atlas => {
                const running = ['queued', 'running'].includes(atlas.status);
                return <tr key={atlas.atlasId}>
                    <td>{atlas.atlasId}</td><td><strong>{atlas.atlasName}</strong>
                        <small className="d-block text-muted">{atlas.status === 'draft' ? 'Needs merge' : atlas.status}</small>
                        {atlas.error && <small className="d-block text-danger">{atlas.error}</small>}
                    </td>
                    <td><Button size="lg" variant="outline-primary" onClick={() => setSelected(atlas)}>{atlas.recordings.length} recordings</Button></td>
                    <td>{atlas.sizeBytes ? SystemUtils.GetSizeString(atlas.sizeBytes) : '—'}</td>
                    <td><Button size="lg" disabled={!atlas.mergedRevision} onClick={() => setProcessing(atlas)}>Process / outputs</Button>{atlas.processingBusy && <small className="d-block text-muted">Processing…</small>}</td>
                    <td><Button size="lg" disabled={!atlas.viewerPath || busy === atlas.atlasId} onClick={() => open(atlas)}>Open</Button>
                        {!!atlas.viewerPath && atlas.viewerRevision !== atlas.revision && <small className="d-block text-muted">Previous merge</small>}</td>
                    <td><Button size="lg" disabled={!editable || running || atlas.processingBusy || atlas.recordings.length < 2 || busy === atlas.atlasId} onClick={() => merge(atlas)}>
                        {atlas.status === 'queued' ? 'Queued' : atlas.status === 'running' ? 'Merging…' : atlas.status === 'failed' ? 'Retry Merge' : atlas.mergedRevision === atlas.revision && !atlas.viewerPath ? 'Prepare viewer' : 'Merge'}</Button>
                        {editable && atlas.status === 'queued' && <Button variant="link" onClick={() => wake().catch(e => setError(e.message))}>Start pipeline</Button>}
                    </td>
                </tr>;
            })}
                {!rows.length && <tr><td colSpan="7">{loading ? 'Loading atlases…' : 'No atlases found. Create an atlas to select recordings.'}</td></tr>}
            </tbody>
        </Table></div>
        {selected && <AtlasMapsPopup key={selected.atlasId + ':' + selected.revision} atlas={selected} editable={editable && !selected.processingBusy && !['queued', 'running'].includes(selected.status)}
            onClose={() => setSelected(null)} onSaved={saved => { setRows(current => current.map(atlas => atlas.atlasId === saved.atlasId ? saved : atlas)); setSelected(null); refresh(); }} />}
        {processing && <AtlasProcessPopup atlas={processing} editable={editable} onClose={() => { setProcessing(null); refresh(); }} onUpdated={refresh} openAtlas={openAtlas} />}
        <Modal className="atlasModal" show={create} onHide={() => busy !== 'create' && setCreate(false)} centered backdrop="static">
            <Form onSubmit={createAtlas}><Modal.Header closeButton={busy !== 'create'}><Modal.Title>New Atlas</Modal.Title></Modal.Header>
                <Modal.Body>{error && <Alert variant="danger">{error}</Alert>}<Form.Label htmlFor="atlasName">Atlas Name</Form.Label>
                    <Form.Control id="atlasName" autoFocus required maxLength={255} pattern="[A-Za-z0-9_][A-Za-z0-9_.-]*" value={name} disabled={busy === 'create'} onChange={e => setName(e.target.value)} />
                    <Form.Text muted>Use letters, numbers, dots, underscores or hyphens. The name also identifies the atlas output folder.</Form.Text>
                </Modal.Body><Modal.Footer><Button variant="secondary" disabled={busy === 'create'} onClick={() => setCreate(false)}>Cancel</Button>
                    <Button type="submit" disabled={busy === 'create'}>{busy === 'create' ? 'Creating…' : 'Create'}</Button></Modal.Footer>
            </Form>
        </Modal>
    </div>;
}
